<template>
    <div class="gmf-dm-convo-header">
        <CompactProfileCard :profile="customer" :hideAttributes="true" :hideStats="true" />
    </div>
    <div class="gmf-dm-convo-list">
        <div v-for="message in conversation" :key="message.uuid" class="gmf-dm-convo-message" :class="{
            'gmf-dm-convo-message-mine': message.customer.id === state.profile.id,
            'gmf-dm-convo-message-chained': chainedMessageMap.next[message.uuid],
        }">
            <div class="gmf-dm-convo-message-avatar-container">
                <Avatar :customer="message.customer" v-if="! chainedMessageMap.next[message.uuid]" />
            </div>
            <div class="gmf-dm-convo-message-content">
                <div class="gmf-dm-convo-message-header" v-if="! chainedMessageMap.next[message.uuid]">
                    <div class="gmf-dm-convo-message-name">{{ message.customer.name }}</div>
                </div>
                <CommentText class="gmf-dm-convo-message-bubble" :value="message.message" />
                <div class="gmf-dm-convo-message-footer" v-if="! chainedMessageMap.prev[message.uuid]">
                    <div class="gmf-text-xs ">{{ timeAgo(message.createdAt) }}</div>
                </div>
            </div>
        </div>
        <div class="gmf-alert gmf-alert-warn" v-if="cautionText" v-text="cautionText" />
    </div>
    <div class="gmf-dm-convo-footer">
        <TextArea placeholder="Aa" v-model="inputText" @keydown.enter.exact.stop.prevent="send" />
        <button class="gmf-btn gmf-btn-primary gmf-btn-sm" @click="send"><Icon icon="send" /></button>
    </div>
</template>

<style>
.gmf-dm-conversation-modal {
    padding: 0 !important;
    overflow: hidden !important;
    width: 700px;
    max-width: 100%;
    background: var(--gmf-col-lightgray);
    overflow: hidden;
    max-height: 100%;
    display: flex;
    flex-direction: column;
}

/* header */
.gmf-dm-convo-header {
    background: var(--gmf-col-white);
    padding: var(--gmf-p-4);
}
.gmf-dm-convo-header .gmf-compact-profile-card-avatar .gmf-avatar {
    width: 40px;
    height: 40px;
}
.gmf-dm-convo-header .gmf-compact-profile-card-nameandstats .gmf-subheader {
    margin: 0;
    color: var(--gmf-col-black);
}

/* footer */
.gmf-dm-convo-footer {
    background: var(--gmf-col-white);
    padding: var(--gmf-p-4);
    display: flex;
    align-items: stretch;
    gap: var(--gmf-p-3);
}
.gmf-dm-convo-footer .gmf-textarea {
    flex-grow: 1;
}
.gmf-dm-convo-footer .gmf-textarea textarea {
    resize: none;
    min-height: 100%;
    vertical-align: bottom;
}
.gmf-dm-convo-footer .gmf-btn {
    align-self: flex-end;
    line-height: 0;
    border-radius: 100px;
    width: 40px;
    height: 40px;
    position: relative;
}
.gmf-dm-convo-footer .gmf-btn .gmf-icon {
    --gmf-icon-color: var(--gmf-col-white);
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    padding: 8px;
}

/* messages */
.gmf-dm-convo-list {
    background: var(--gmf-col-lightgray);
    display: flex;
    flex-direction: column-reverse;
    overflow: hidden;
    padding: var(--gmf-p-4);
    overflow-y: scroll;
    flex-shrink: 1;
    overscroll-behavior: contain;
}
.gmf-dm-convo-message {
    display: flex;
    gap: var(--gmf-p-3);
    overflow: hidden;
    flex-shrink: 0;
    padding-top: var(--gmf-p-5);
}
.gmf-dm-convo-message-chained {
    padding-top: var(--gmf-p-1);
}
.gmf-dm-convo-message:last-child {
    padding-top: 0 !important;
}
.gmf-dm-convo-message-avatar-container {
    width: 64px;
    flex-shrink: 0;
    line-height: 0;
}
.gmf-dm-convo-message .gmf-avatar {
    width: 64px;
    height: 64px;
}
.gmf-dm-convo-message-bubble {
    padding: var(--gmf-p-4);
    background: var(--gmf-card-col-ongray);
    border-radius: var(--gmf-rounding);
    overflow: hidden;
    white-space: pre-wrap;
}

/* message header */
.gmf-context .gmf-dm-convo-message-header {
    margin-bottom: var(--gmf-p-1);
}
.gmf-context .gmf-dm-convo-message-name {
    font-weight: bold;
}

/* message footer */
.gmf-context .gmf-dm-convo-message-footer {
    margin-top: var(--gmf-p-1);
}

/* your own messages */
.gmf-dm-convo-message-mine {
    flex-direction: row-reverse;
}
.gmf-dm-convo-message-mine .gmf-dm-convo-message-header,
.gmf-dm-convo-message-mine .gmf-dm-convo-message-footer {
    text-align: right;
}
.gmf-dm-convo-message-mine .gmf-dm-convo-message-bubble {
    background: var(--gmf-brand-color);
    color: var(--gmf-col-white);
}
.gmf-dm-convo-message-mine .gmf-link {
    color: var(--gmf-col-white);
}

@media (max-width: 480px) {
    .gmf-context .gmf-dm-convo-message-name {
        font-size: var(--gmf-text-small-font-size);
    }
    .gmf-dm-convo-message-bubble {
        padding: var(--gmf-p-3) var(--gmf-p-4);
    }
    .gmf-dm-convo-message-avatar-container {
        width: 40px;
    }
    .gmf-dm-convo-message .gmf-avatar {
        width: 40px;
        height: 40px;
    }

    .gmf-safari.gmf-dm-conversation-modal {
        width: unset;
        height: unset;
        position: absolute;
        inset: 8px;
        bottom: 40% !important;
    }
}

</style>

<script>
import callApi from '@/utils/callApi';
import Avatar from '@/components/Avatar.vue';

import timeAgo from '@/utils/timeAgo';
import state from '@/state';
import CompactProfileCard from '@/components/CompactProfileCard.vue';
import TextArea from '@/components/input/TextArea.vue';
import Icon from '@/components/Icon.vue';
import CommentText from '@/components/Comment/CommentText';

var ua = window.navigator.userAgent;
var iOS = ua.match(/iPad/i) || ua.match(/iPhone/i);
var webkit = ua.match(/WebKit/i);
var iOSSafari = iOS && webkit && !ua.match(/CriOS/i);

const scrollRoot = document.documentElement;

export default {
    inject: ['otherMerchantCustomerId'],
    data() {
        return {
            state,
            conversation: [],
            customer: {},

            inputText: '',
            interval: null,
        };
    },
    async mounted() {
        this.interval = setInterval(this.loadNewMessages, 3000);

        if (iOSSafari) {
            this.previousScroll = scrollRoot.scrollTop; // Spara scrollpositionen
        }

        this.load();

        const res = await callApi('GET', '/v1/client/profile/' + this.otherMerchantCustomerId);
        this.customer = await res.json();
    },
    unmounted () {
        clearInterval(this.interval);

        if (iOSSafari) {
            // scrolla tillbaka, för ios får för sig att scrolla hela sidan när man öppnar tangentbordet
            scrollRoot.scrollTop = this.previousScroll;
        }
    },
    computed: {
        cautionText () {
            return this.state.t('direct_messages.caution_text');
        },
        chainedMessageMap () {
            let next = {};
            let prev = {};

            const compactTime = 5 * 60000; // 5 min

            let lastSenderId = null;
            let lastSentTime = 0;
            let lastUuid = null;

            for (let i = this.conversation.length - 1; i >= 0; --i) {
                const message = this.conversation[i];
                const createdAt = new Date(message.createdAt);
                if (message.customer.id === lastSenderId && createdAt - lastSentTime < compactTime) {
                    next[message.uuid] = true;
                    if (lastUuid) {
                        prev[lastUuid] = true;
                    }
                    lastUuid = message.uuid;
                    continue;
                }
                lastUuid = message.uuid;
                lastSenderId = message.customer.id;
                lastSentTime = createdAt;
            }

            return {
                next,
                prev,
            };
        },
    },
    methods: {
        async send () {
            const text = this.inputText.trim();
            setTimeout(() => {
                this.inputText = '';
            }, 0);
            if (! text) {
                return;
            }
            this.conversation.unshift({
                uuid: 'ewtestsesdfsdsd' + Math.random(),
                "customer": this.state.profile,
                "message": text,
                "createdAt": new Date(),
            });
            await callApi('POST', '/v1/client/dm/conversations/' + this.otherMerchantCustomerId, {
                message: text,
            });
        },
        timeAgo (date) {
            let d = new Date(date);
            return timeAgo(d.getTime()/1000);
        },
        async load() {
            const res = await callApi('GET', '/v1/client/dm/conversations/' + this.otherMerchantCustomerId);
            const result = await res.json();
            this.conversation = result.conversation;
        },
        async loadNewMessages() {
            if (document.visibilityState === 'hidden' || ! this.otherMerchantCustomerId) {
                return;
            }
            // @TODO better load method
            const res = await callApi('GET', '/v1/client/dm/conversations/' + this.otherMerchantCustomerId);
            const result = await res.json();
            this.conversation = result.conversation;
        },
    },
    components: { Avatar, CompactProfileCard, TextArea, Icon, CommentText }
};
</script>
